import { FormEvent, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { faAt, faBuilding, faGift, faPhone, faWandMagicSparkles, faUser } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import InputField from "../InputField/InputField"
import { GenericDropDown } from "../Dropdown/Dropdown"
import { useGetLandingTracking } from "../../hooks/Landing/useGetLandingTracking"
import { PostRequest } from "../../services/ApiClientService"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { PATH_MAP } from "../../configuration/PathMap"
import { isValidBrazilianPhoneWithDdd, normalizePhoneDigits } from "../../utils/phoneFormat"
import { trackLeadConversion, trackMarketingEvent } from "../../analytics/tracking"
import Animate, { AnimationTypes } from "../Animate/Animate"

type LeadFormState = { name: string; businessName: string; email: string; phone: string; trafficSource: string }

interface LeadCaptureFormProps {
  className?: string
  i18nPrefix?: string
  requireEmailField?: boolean
  showBusinessNameField?: boolean
  source?: string
  showEmailField?: boolean
  showTrafficSourceField?: boolean
  trafficSourceFallback?: string
  variant?: "default" | "compact"
}

const initialState: LeadFormState = { name: "", businessName: "", email: "", phone: "", trafficSource: "" }

const getQueryValue = (params: URLSearchParams, key: string) => params.get(key)?.trim() || ""

const buildTrafficSource = (fallback: string) => {
  if (typeof window === "undefined") return fallback

  const params = new URLSearchParams(window.location.search)

  const utmSource = getQueryValue(params, "utm_source")

  const utmMedium = getQueryValue(params, "utm_medium")

  const utmCampaign = getQueryValue(params, "utm_campaign")

  const utmContent = getQueryValue(params, "utm_content")

  const parts = [
    utmSource || fallback,
    utmMedium && `mid:${utmMedium}`,
    utmCampaign && `camp:${utmCampaign}`,
    utmContent && `ad:${utmContent}`,
  ].filter(Boolean)

  return parts.join(" | ").slice(0, 80)
}

export default function LeadCaptureForm({
  className = "",
  i18nPrefix = "lead_form",
  requireEmailField = true,
  showBusinessNameField = true,
  source = "landing_lead_capture",
  showEmailField = true,
  showTrafficSourceField = true,
  trafficSourceFallback = "Landing page",
  variant = "default",
}: LeadCaptureFormProps) {
  const { t } = useTranslation("landpage")

  const { handleGetLandingTracking } = useGetLandingTracking()

  const [showForm, setShowForm] = useState(true)

  const trafficSource = useMemo(() => buildTrafficSource(trafficSourceFallback), [trafficSourceFallback])

  const shouldAutofillTrafficSource = !showTrafficSourceField

  const [form, setForm] = useState<LeadFormState>(() => ({
    ...initialState,
    trafficSource: shouldAutofillTrafficSource ? trafficSource : "",
  }))

  const [isSubmitting, setIsSubmitting] = useState(false)

  const [feedback, setFeedback] = useState<string | null>(null)

  const [isSuccessFeedback, setIsSuccessFeedback] = useState(false)

  const sources = useMemo(() => t("lead_form.traffic_sources", { returnObjects: true }) as string[], [t])

  const text = (key: string) => t(`${i18nPrefix}.${key}`)

  useEffect(() => {
    handleGetLandingTracking()
      .then((s) => setShowForm(s.showLeadCaptureForm))
      .catch(() => setShowForm(true))
  }, [handleGetLandingTracking])

  useEffect(() => {
    if (!shouldAutofillTrafficSource) return

    setForm((current) => ({ ...current, trafficSource: current.trafficSource || trafficSource }))
  }, [shouldAutofillTrafficSource, trafficSource])

  const isPhoneValid = isValidBrazilianPhoneWithDdd(form.phone)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!isPhoneValid) return setFeedback(t("lead_form.invalid_phone"))

    const effectiveTrafficSource = form.trafficSource || trafficSource

    if (showTrafficSourceField && !form.trafficSource.trim()) return setFeedback(t("lead_form.invalid_traffic_source"))

    setIsSubmitting(true)

    setFeedback(null)

    setIsSuccessFeedback(false)

    trackMarketingEvent("landing_lead_submit_attempt", {
      source,
      traffic_source: effectiveTrafficSource,
    })

    try {
      const response = await PostRequest(API_ROUTES.LANDING_LEAD.CREATE, {
        name: form.name,
        businessName: form.businessName,
        email: form.email,
        phoneNumber: form.phone,
        trafficSource: effectiveTrafficSource,
        formName: i18nPrefix,
      })

      if (!response.success) throw new Error(response.message)

      setForm({ ...initialState, trafficSource: shouldAutofillTrafficSource ? trafficSource : "" })

      setFeedback(text("success"))

      setIsSuccessFeedback(true)

      trackMarketingEvent("landing_lead_submit_success", {
        source,
        traffic_source: effectiveTrafficSource,
      })

      trackLeadConversion({
        content_name: source,
        status: "lead_created",
        traffic_source: effectiveTrafficSource,
      })
    } catch {
      setFeedback(t("lead_form.error"))

      setIsSuccessFeedback(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!showForm) return null

  const isCompact = variant === "compact"

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
      <section
        className={`${isCompact ? "w-full" : "mx-auto mt-8 w-full max-w-[1280px] px-6 pb-4"} ${className}`}
        id="lead-capture"
      >
        <div
          className={`bg-gradient-to-br from-white to-orange-100 shadow-sm ${
            isCompact ? "rounded-2xl  p-5 md:p-6" : "rounded-xl  p-4 lg:rounded-xl"
          }`}
        >
          {/* <h2 className={`${isCompact ? "text-xl" : "text-2xl"} text-center md:text-left font-black text-slate-900`}>
            {text("title")}
          </h2>

         <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">{text("description")}</p>*/}

          <div className="rounded-lg border-2 border-emerald-400 bg-white px-4 py-3">
            <div className="flex items-center gap-2 text-emerald-800">
              <FontAwesomeIcon icon={faGift} />

              <span className="text-sm font-extrabold">{text("free_setup_badge")}</span>
            </div>

            <p className="mt-1 text-sm text-emerald-700">{text("free_setup_description")}</p>
          </div>

          <form
            className={`mt-6 grid grid-cols-1 gap-4 ${isCompact ? "md:grid-cols-1" : "md:grid-cols-2 xl:grid-cols-3"}`}
            onSubmit={handleSubmit}
          >
            <InputField
              type="text"
              icon={faUser}
              required
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={text("fields.name")}
              onInvalidMessage={t("lead_form.invalid_required")}
              descriptionKey="interactionDescriptions:components.landPage.leadCaptureForm.input.textFieldsName"
            />

            {showBusinessNameField && (
              <InputField
                type="text"
                icon={faBuilding}
                required
                value={form.businessName}
                onChange={(e) => setForm((p) => ({ ...p, businessName: e.target.value }))}
                placeholder={text("fields.business")}
                onInvalidMessage={t("lead_form.invalid_required")}
                descriptionKey="interactionDescriptions:components.landPage.leadCaptureForm.input.textFieldsBusiness"
              />
            )}

            {showEmailField && (
              <InputField
                type="email"
                icon={faAt}
                required={requireEmailField}
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder={text("fields.email")}
                onInvalidMessage={t("lead_form.invalid_email")}
                descriptionKey="interactionDescriptions:components.landPage.leadCaptureForm.input.textFieldsEmail"
              />
            )}

            <InputField
              type="text"
              icon={faPhone}
              required
              maxLength={11}
              inputMode="numeric"
              pattern="[0-9]{11}"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: normalizePhoneDigits(e.target.value) }))}
              placeholder={text("fields.phone")}
              onInvalidMessage={t("lead_form.invalid_phone")}
              descriptionKey="interactionDescriptions:components.landPage.leadCaptureForm.input.textFieldsPhone"
            />

            {showTrafficSourceField && (
              <GenericDropDown
                data={[t("lead_form.fields.traffic_source"), ...sources]}
                initialState={form.trafficSource || 0}
                handlerFuncion={(event) =>
                  setForm((p) => ({
                    ...p,
                    trafficSource:
                      event.target.value === t("lead_form.fields.traffic_source") ? "" : event.target.value,
                  }))
                }
                descriptionKey="interactionDescriptions:components.landPage.leadCaptureForm.select.select"
              />
            )}

            <div className={isCompact ? "" : "md:col-span-2 xl:col-span-3"}>
              <button
                disabled={isSubmitting}
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-600 to-orange-500 px-5 py-3 text-sm font-black text-white shadow-lg shadow-red-500/20 transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-70"
                data-interaction-description-key="interactionDescriptions:components.landPage.leadCaptureForm.button.submitting"
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                {isSubmitting ? t("lead_form.submitting") : text("submit")}
              </button>
            </div>
          </form>

          {feedback && (
            <div className="mt-4 space-y-2">
              <p className={`text-sm font-semibold ${isSuccessFeedback ? "text-emerald-700" : "text-red-700"}`}>
                {feedback}
              </p>
              {isSuccessFeedback && (
                <a
                  className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-bold text-orange-700 hover:bg-orange-100"
                  href={PATH_MAP.ROOT_SIGN_UP}
                  data-interaction-description-key="interactionDescriptions:components.landPage.leadCaptureForm.link.link"
                >
                  {text("success_self_signup_cta")}
                </a>
              )}
              {isSuccessFeedback && <p className="text-xs text-slate-500">{text("success_self_signup")}</p>}
            </div>
          )}
        </div>
      </section>
    </Animate>
  )
}
